/**
 * Год какого животного по китайскому календарю?
 * ====================================
 * TODO Combination of sign/animal/color
 * TODO Predictions for most common/future years
 * TODO Как встречать 2024-й Год Дракона
 * TODO Что приготовить на стол на Новый год
 * TODO Who is lucky in this year
 */
import { LoaderFunctionArgs, MetaFunction } from "react-router";
import { useLoaderData, useLocation } from "react-router";
import { TFunction } from "i18next";
import { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import invariant from "tiny-invariant";

import Button from "~/components/Button";
import NumberInput from "~/components/forms/NumberInput";
import H2 from "~/components/H2";
import Icon from "~/components/Icon";
import AdditionalInfo from "~/components/tool-page/AdditionalInfo";
import AdditionalInfoSection from "~/components/tool-page/AdditionalInfoSection";
import Result from "~/components/tool-page/Result";
import ToolForm from "~/components/tool-page/ToolForm";
import ToolHeader from "~/components/tool-page/ToolHeader";
import {
  ChineseZodiacCompatability,
  getChineseYear,
  getChineseZodiacCompatability
} from "~/helpers/dates.server";
import { formatLongDate } from "~/helpers/dates";
import { makeTitle } from "~/helpers/pageTitle";
import { createArticleSchema } from "~/helpers/schemaOrg";
import { getLocale } from "~/i18n";
import { getTranslator } from "~/i18next.server";
import CalculatorLayout from "~/routes/layouts/components/CalculatorLayout";
import ToolHeaderNew from "~/components/tool-page/ToolHeaderNew";
import AdditionalContentLayout from "~/routes/layouts/components/AdditionalContentLayout";

const CATEGORY = "dates";
const TKEY = "animal_of_the_year";
const ROUTE_ENDING = "animal-of-the-year";


export const meta: MetaFunction<typeof loader> = function({ data }) {
  invariant(data);

  const meta = [
    { title: makeTitle(data?.meta.title) },
    { name: "description", content: data?.meta.description },
    createArticleSchema(
      data?.meta.locale as string,
      data?.meta.title as string,
      data?.meta.images ?? [],
      new Date(2024, 4, 24)
    ),
  ];

  if (data.meta.shouldIndex === false) {
    meta.push({ name: "robots", content: "noindex" });
  }

  return meta;
};

export const handle = {
  localisedLinks: ["en", "ru", "de", "fr", "es", "pt", "it", "pl", "ar", "th"],
  i18n: ["translations", "dates", "animal-of-the-year"]
};

interface Result {
  animal: string;
  startDate: string | null;
  endDate: string | null;
  planet: string;
  stem: string;
  color: string;
  compatibility: ChineseZodiacCompatability;
}

function createTitle(year: number | null, t: TFunction): string {
  return year === null
    ? t(`${TKEY}.title`)
    : t(`${TKEY}.title_at_year`, { year });
}

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const year: number | null = params.slug ? Number(params.slug) : null;

  if (year !== null && Number.isNaN(year)) {
    throw new Response(null, {
      status: 404,
      statusText: "Not Found"
    });
  }


  const locale = getLocale(request.url) ?? "";
  const t = await getTranslator(request, "animal-of-the-year");
  const tDates = await getTranslator(request, "dates");


  const title = createTitle(year, t);

  const breadcrumbs = [
    { title: `catalog.categories.${CATEGORY}`, to: `${CATEGORY}` },
    { title: title, to: `${CATEGORY}/${ROUTE_ENDING}` }
  ];


  let result: Result | null = null;

  const metaImages: string[] = [];

  const chineseYear = year ? getChineseYear(year) : null;

  const metaDescription: string = chineseYear
    ? t(`${TKEY}.description_at_year`, { year, sign: tDates(`chinese_zodiac.${chineseYear.animal}`) })
    : t(`${TKEY}.description`);

  const shouldIndex = year === null || (year > 1930 && year < 2050);

  if (year !== null && chineseYear !== null) {
    breadcrumbs.push({ title, to: `${CATEGORY}/${ROUTE_ENDING}/${year}` });

    result = {
      animal: chineseYear.animal,
      // animal: getChineseZodiac(year),
      startDate: chineseYear.startDate?.toLocaleDateString(locale, { month: "long", day: "numeric" }) ?? null,
      endDate: chineseYear.endDate ? formatLongDate(chineseYear.endDate, locale) : null,
      planet: chineseYear.planet,
      stem: chineseYear.stem,
      color: chineseYear.color,
      compatibility: getChineseZodiacCompatability(chineseYear.animal)
    };
  }


  return {
    year,
    result,
    meta: {
      shouldIndex: shouldIndex,
      locale,
      currentUrl: request.url,
      title: title,
      description: metaDescription,
      breadcrumbs,
      images: metaImages
    }
  };
};

function calculate(e: FormEvent<HTMLFormElement>) {
  e.preventDefault();

  const formData = new FormData(e.currentTarget);

  // Use formData to get your values, e.g., number, unit, direction, date
  const slug = formData.get("year") as string | null;

  // Assume you have a "createSlug" function that accepts `resultingDate` as parameter
  // Analyze the current URL and replace or add the slug accordingly
  let currentPathname = window.location.pathname;
  const slugPattern = new RegExp(`/${ROUTE_ENDING}/?(.*)$`);

  if (slugPattern.test(currentPathname)) {
    // Replace existing slug
    currentPathname = currentPathname.replace(slugPattern, `/${ROUTE_ENDING}/${slug}`);
  } else {
    // Add slug to the pathname
    currentPathname = currentPathname.endsWith("/") ? `/${ROUTE_ENDING}/${slug}` : `/${ROUTE_ENDING}/${slug}/`;
  }

  // Redirect to the new path if it changed
  const newUrl = window.location.origin + currentPathname;
  if (window.location.href === newUrl) {
    return;
  }

  window.location.href = newUrl;
}

export default function Index() {
  const {
    year,
    meta,
    result
  } = useLoaderData<typeof loader>();

  const { t } = useTranslation("translations");
  const { t: tDates } = useTranslation("dates");
  const { t: tCalc } = useTranslation("animal-of-the-year");

  const locale = getLocale(useLocation().pathname);
  invariant(locale);


  const questionPart1 = tCalc(`${TKEY}.question_part1`);

  return (
    <>
      <CalculatorLayout>
      <ToolHeaderNew
        name={meta.title}
        description={meta.description}
      />

      {result !== null && year !== null ? (
        <Result color="positive">
          <H2 className="first-letter:capitalize text-lg pb-6">{t("answer")}:</H2>

          <p dangerouslySetInnerHTML={{
            __html: tCalc(`${TKEY}.result_chinese_zodiac`, { sign: tDates(`chinese_zodiac.${result.animal}`) })
          }} />
          {result.startDate && result.endDate ? (
            <>
              <p dangerouslySetInnerHTML={{
                __html: tCalc(`${TKEY}.result_lunar_year_start_end`, { year, starts: result.startDate, ends: result.endDate })
              }} />
              <p>{tDates("chinese_year_color.color")}: {tDates(`chinese_year_color.${result.color}`)}</p>
              <p>{tDates("chinese_year_stem.stem")}: {tDates(`chinese_year_stem.${result.stem}`)}</p>
              <p>{tDates("chinese_year_planet.planet")}: {tDates(`chinese_year_planet.${result.planet}`)}</p>
            </>
          ) : null}
        </Result>
      ) : null}

      <ToolForm onSubmit={calculate}>
        <div className="w-full xl:w-auto flex  flex-col md:flex-row md:items-center md:justify-start gap-4">
          {questionPart1.length > 0
            ? <span className="font-semibold">{tCalc(`${TKEY}.question_part1`)}</span>
            : null
          }

          <div className="">
            <NumberInput
              name="year"
              value={year ?? (new Date()).getFullYear()}
              ariaLabel={t(`${TKEY}.input_year`)}
            />
          </div>
          <span className="hidden md:inline font-semibold">{tCalc(`${TKEY}.question_part2`)}</span>
        </div>

        <Button className="w-full" isSubmit={true}>
          <Icon v="keypad" className="pr-2" /> {t("do_calculate")}
        </Button>
      </ToolForm>
      </CalculatorLayout>

      <AdditionalContentLayout>
      <AdditionalInfo>
        <AdditionalInfoSection>
          <H2>{tCalc(`${TKEY}.info_about_h`)}</H2>
          <p dangerouslySetInnerHTML={{ __html: tDates(`chinese_zodiac.about`) }} />
        </AdditionalInfoSection>

        {result === null || year === null ? null : (
          <>
          <AdditionalInfoSection>
            <H2 anchor="chinese-zodiac">
              {tCalc(`${TKEY}.info_meaning_of_sign`, { sign: tDates(`chinese_zodiac.${result.animal}`) })}
            </H2>
            <p>{tDates(`chinese_zodiac.${result.animal}_about`)}</p>
          </AdditionalInfoSection>
          <AdditionalInfoSection>
            <H2>{tCalc(`${TKEY}.info_compatibility_h`)}</H2>
            <p>{tCalc(`${TKEY}.info_compatibility_p`)}</p>
            <table className="table table-auto">
              <tbody>
                <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                  <td className="py-4">
                    {tCalc(`${TKEY}.info_compatibility_yes`)}
                  </td>
                  <td className="py-4">
                    {result.compatibility.compatible.map(key => tDates(`chinese_zodiac.${key}`)).join(", ")}
                  </td>
                </tr>
                <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                  <td className="py-4">
                    {tCalc(`${TKEY}.info_compatibility_possible`)}
                  </td>
                  <td className="py-4">
                    {result.compatibility.possible.map(key => tDates(`chinese_zodiac.${key}`)).join(", ")}
                  </td>
                </tr>
                <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                  <td className="py-4">
                    {tCalc(`${TKEY}.info_compatibility_no`)}
                  </td>
                  <td className="py-4">
                    {result.compatibility.incompatible.map(key => tDates(`chinese_zodiac.${key}`)).join(", ")}
                  </td>
                </tr>
              </tbody>
            </table>
          </AdditionalInfoSection>
          </>
        )}
      </AdditionalInfo>
      </AdditionalContentLayout>
    </>
  );
}
